import { useState } from 'react';
import { useAddHolidayMutation } from 'redux/features/holidayApi';

import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const AddEventModal = ({ isOpen, onClose, selectedDate, onSuccess }) => {
  const [title, setTitle] = useState('');
  const [type, setType] = useState('Event Celebration');
  const [error, setError] = useState('');

  // API mutation hook
  const [addHoliday, { isLoading }] = useAddHolidayMutation();

  // Handle type change and auto-fill title for Saturday Off
  const handleTypeChange = (newType) => {
    setType(newType);
    setError(''); // Clear any existing errors

    if (newType === 'SATURDAY_OFF') {
      setTitle('Saturday Off');
    } else if (type === 'SATURDAY_OFF' && newType !== 'SATURDAY_OFF') {
      // Clear title if switching away from Saturday Off
      setTitle('');
    }
  };

  const eventTypes = [
    {
      value: 'EVENT_CELEBRATION',
      label: 'Event Celebration',
      icon: 'Star',
      color: 'text-blue-600',
    },
    { value: 'FULL_HOLIDAY', label: 'Full Holiday', icon: 'Calendar', color: 'text-red-600' },
    { value: 'HALF_HOLIDAY', label: 'Half Holiday', icon: 'Clock', color: 'text-orange-600' },
    { value: 'SATURDAY_OFF', label: 'Saturday Off', icon: 'Home', color: 'text-green-600' },
  ];

  const handleSave = async () => {
    // Clear previous errors
    setError('');

    // Validate title
    if (!title?.trim()) {
      setError('Please enter an event title.');
      return;
    }

    try {
      // Format date as ISO string
      const formattedDate = selectedDate
        ? new Date(selectedDate - selectedDate.getTimezoneOffset() * 60000).toISOString()
        : new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString();

      // Prepare API payload
      const payload = {
        title: title.trim(),
        type: type,
        date: formattedDate,
      };

      // Call API
      await addHoliday(payload).unwrap();

      // Call onSuccess callback if provided (for showing success message)
      if (onSuccess) {
        onSuccess('Event added successfully!');
      }

      // Close and reset modal
      handleClose();
    } catch (err) {
      // Handle API errors
      console.error('Failed to add holiday:', err);

      // Set user-friendly error message
      if (err?.data?.message) {
        setError(err.data.message);
      } else if (err?.status === 409) {
        setError('An event already exists on this date.');
      } else if (err?.status === 400) {
        setError('Invalid event data. Please check your inputs.');
      } else {
        setError('Failed to add event. Please try again.');
      }
    }
  };

  const handleClose = () => {
    // Don't allow closing while saving
    if (isLoading) return;

    // Reset form
    setTitle('');
    setType('Event Celebration');
    setError('');
    onClose?.();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center">
        {/* Background overlay */}
        <div
          className="fixed inset-0 transition-opacity bg-black bg-opacity-50"
          onClick={handleClose}
        ></div>

        {/* Modal panel */}
        <div className="relative inline-block w-full max-w-md p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-surface shadow-xl rounded-2xl border border-border">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-semibold text-text-primary">Add New Event</h3>
              <p className="text-sm text-text-secondary mt-1">
                {selectedDate?.toLocaleDateString('en-US', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </p>
            </div>
            <button
              onClick={handleClose}
              disabled={isLoading}
              className="p-2 text-text-secondary hover:text-text-primary hover:bg-secondary-100 rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Icon name="X" size={20} />
            </button>
          </div>

          {/* Error Alert */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-2">
              <Icon name="AlertCircle" size={16} className="text-red-600 mt-0.5" />
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {/* Form */}
          <div className="space-y-4">
            {/* Title Input */}
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Event Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => {
                  setTitle(e?.target?.value);
                  setError(''); // Clear error on input change
                }}
                placeholder="Enter event title..."
                disabled={type === 'Saturday Off' || isLoading}
                className={`w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200 ${
                  type === 'Saturday Off' || isLoading ? 'bg-gray-100 cursor-not-allowed' : ''
                }`}
                autoFocus={type !== 'Saturday Off'}
              />
              {type === 'Saturday Off' && (
                <p className="text-xs text-text-secondary mt-1">
                  Title is automatically set for Saturday Off events
                </p>
              )}
            </div>

            {/* Type Selection */}
            <div>
              <label className="block text-sm font-medium text-text-primary mb-3">
                Event Type <span className="text-red-500">*</span>
              </label>
              <div className="space-y-2">
                {eventTypes?.map((eventType) => (
                  <label
                    key={eventType?.value}
                    className={`flex items-center space-x-3 cursor-pointer ${
                      isLoading ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                  >
                    <input
                      type="radio"
                      name="eventType"
                      value={eventType?.value}
                      checked={type === eventType?.value}
                      onChange={(e) => handleTypeChange(e?.target?.value)}
                      disabled={isLoading}
                      className="w-4 h-4 text-primary border-border focus:ring-primary-500 disabled:cursor-not-allowed"
                    />
                    <div className="flex items-center space-x-2">
                      <Icon name={eventType?.icon} size={16} className={eventType?.color} />
                      <span className="text-sm text-text-primary">{eventType?.label}</span>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex items-center space-x-3 mt-6">
            <button
              onClick={handleClose}
              disabled={isLoading}
              className="flex-1 px-4 py-2 text-text-secondary bg-secondary-100 hover:bg-secondary-200 rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <Button
              onClick={handleSave}
              disabled={isLoading || (!title?.trim() && type !== 'Saturday Off')}
              className="flex-1 px-4 py-2 text-white rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2"
              loading={isLoading}
            >
              <Icon name="Check" size={16} />
              <span>Save Event</span>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddEventModal;
